import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { Headache, Tension, Lloro } from '../../types';
import './TrendChart.css';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface TrendChartProps {
  healthMode: 'headaches' | 'tensions' | 'lloros';
  headaches: Headache[];
  tensions: Tension[];
  lloros: Lloro[];
  days?: number;
}

const TrendChart: React.FC<TrendChartProps> = ({
  healthMode,
  headaches,
  tensions,
  lloros,
  days = 30
}) => {
  // Generar datos de los últimos N días
  const chartData = useMemo(() => {
    const today = new Date();
    const labels: string[] = [];
    const data: number[] = [];
    const secondaryData: number[] = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayLabel = date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
      labels.push(dayLabel);

      if (healthMode === 'headaches') {
        const dayHeadaches = headaches.filter(h => h.date.startsWith(dateStr));
        data.push(dayHeadaches.length);
        // Intensidad promedio del día
        if (dayHeadaches.length > 0) {
          const intensityMap: { [key: string]: number } = { 'leve': 1, 'moderada': 2, 'fuerte': 3 };
          const avgIntensity = dayHeadaches.reduce((sum, h) => {
            return sum + (intensityMap[h.intensity] || 0);
          }, 0) / dayHeadaches.length;
          secondaryData.push(avgIntensity);
        } else {
          secondaryData.push(0);
        }
      } else if (healthMode === 'tensions') {
        const dayTensions = tensions.filter(t => t.date === dateStr);
        data.push(dayTensions.length);
        // Promedio sistólico del día
        if (dayTensions.length > 0) {
          const avgSystolic = dayTensions.reduce((sum, t) => sum + t.systolic, 0) / dayTensions.length;
          secondaryData.push(Math.round(avgSystolic));
        } else {
          secondaryData.push(0);
        }
      } else if (healthMode === 'lloros') {
        const dayLloros = lloros.filter(l => l.date === dateStr);
        data.push(dayLloros.length);
        // Intensidad promedio
        if (dayLloros.length > 0) {
          const intensityMap: { [key: string]: number } = { 'leve': 1, 'moderado': 2, 'fuerte': 3 };
          const avgIntensity = dayLloros.reduce((sum, l) => {
            return sum + (intensityMap[l.intensity] || 0);
          }, 0) / dayLloros.length;
          secondaryData.push(avgIntensity);
        } else {
          secondaryData.push(0);
        }
      }
    }

    return { labels, data, secondaryData };
  }, [healthMode, headaches, tensions, lloros, days]);

  const getChartConfig = () => {
    switch (healthMode) {
      case 'headaches':
        return {
          primaryLabel: 'Dolores registrados',
          secondaryLabel: 'Intensidad promedio',
          primaryColor: 'rgba(99, 102, 241, 0.8)',
          primaryBg: 'rgba(99, 102, 241, 0.1)',
          secondaryColor: 'rgba(239, 68, 68, 0.8)',
          secondaryBg: 'rgba(239, 68, 68, 0.1)',
          title: 'Tendencia de Dolores de Cabeza',
          icon: '🤕'
        };
      case 'tensions':
        return {
          primaryLabel: 'Mediciones',
          secondaryLabel: 'Sistólica promedio',
          primaryColor: 'rgba(16, 185, 129, 0.8)',
          primaryBg: 'rgba(16, 185, 129, 0.1)',
          secondaryColor: 'rgba(245, 158, 11, 0.8)',
          secondaryBg: 'rgba(245, 158, 11, 0.1)',
          title: 'Tendencia de Tensión Arterial',
          icon: '💓'
        };
      case 'lloros':
        return {
          primaryLabel: 'Lloros registrados',
          secondaryLabel: 'Intensidad promedio',
          primaryColor: 'rgba(96, 165, 250, 0.8)',
          primaryBg: 'rgba(96, 165, 250, 0.1)',
          secondaryColor: 'rgba(167, 139, 250, 0.8)',
          secondaryBg: 'rgba(167, 139, 250, 0.1)',
          title: 'Tendencia de Lloros',
          icon: '😢'
        };
    }
  };

  const config = getChartConfig();

  const chartDataConfig = {
    labels: chartData.labels,
    datasets: [
      {
        label: config.primaryLabel,
        data: chartData.data,
        borderColor: config.primaryColor,
        backgroundColor: config.primaryBg,
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        pointHoverRadius: 5,
        yAxisID: 'y'
      },
      {
        label: config.secondaryLabel,
        data: chartData.secondaryData,
        borderColor: config.secondaryColor,
        backgroundColor: config.secondaryBg,
        fill: false,
        tension: 0.4,
        pointRadius: 2,
        pointHoverRadius: 5,
        yAxisID: 'y1',
        borderDash: [5, 5]
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: 12 },
        bodyFont: { size: 11 },
        padding: 10,
        cornerRadius: 8
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 10
          },
          // Mostrar solo algunas etiquetas para no saturar
          autoSkip: true,
          maxTicksLimit: 10
        }
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 10
          },
          stepSize: 1
        },
        title: {
          display: true,
          text: 'Registros',
          font: {
            size: 10
          }
        }
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        beginAtZero: healthMode !== 'tensions',
        min: healthMode === 'tensions' ? 80 : 0,
        max: healthMode === 'tensions' ? 180 : (healthMode === 'headaches' || healthMode === 'lloros' ? 3 : undefined),
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          font: {
            size: 10
          }
        },
        title: {
          display: true,
          text: healthMode === 'tensions' ? 'mmHg' : 'Intensidad',
          font: {
            size: 10
          }
        }
      }
    }
  };

  // Calcular estadísticas resumen
  const summaryStats = useMemo(() => {
    const totalRecords = chartData.data.reduce((a, b) => a + b, 0);
    const avgPerDay = (totalRecords / days).toFixed(1);
    const maxInDay = Math.max(...chartData.data);
    const daysWithRecords = chartData.data.filter(d => d > 0).length;

    return {
      total: totalRecords,
      avgPerDay,
      maxInDay,
      daysWithRecords
    };
  }, [chartData, days]);

  if (summaryStats.total === 0) {
    return (
      <div className="trend-chart-container empty">
        <div className="trend-chart-header">
          <h3>{config.icon} {config.title}</h3>
        </div>
        <div className="no-data-message">
          <p>No hay registros en los últimos {days} días</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trend-chart-container">
      <div className="trend-chart-header">
        <h3>{config.icon} {config.title}</h3>
        <span className="trend-period">Últimos {days} días</span>
      </div>

      <div className="trend-summary">
        <div className="summary-stat">
          <span className="stat-value">{summaryStats.total}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">{summaryStats.avgPerDay}</span>
          <span className="stat-label">Promedio/día</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">{summaryStats.daysWithRecords}</span>
          <span className="stat-label">Días con registros</span>
        </div>
      </div>

      <div className="chart-wrapper">
        <Line data={chartDataConfig} options={options} />
      </div>
    </div>
  );
};

export default TrendChart;
